import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import ForumsByCourse from './ForumsByCourse';
import DiscussionDetails from './DiscussionDetails';
import AddDiscussionForm from './forms/AddDiscussionForm';
import {
  useForumViewMutation,
  useForumDiscussions,
  useForumAccessInfo,
  useForumCanAddDiscussion,
  useForumSubscribeMutation,
  useAddDiscussionMutation,
  usePinDiscussionMutation,
  useToggleFavouriteMutation,
  useLockDiscussionMutation,
  useDiscussionViewMutation,
  type MoodleInstance,
} from '../../../../hooks/useMoodleHooks';
import { MoodleForumDiscussion } from '../../../../types/moodleTypes';
import { selectActiveMoodleInstanceState } from '../../../../support/slices/moodleSlice';
import type { RootState } from '../../../../support/store';

type Props = {
  courseId?: number;
  forumId?: number;
  initialDiscussionId?: number;
  instance?: MoodleInstance;
};

function DiscussionAccordionItem({
                                   discussion,
                                   discussionId,
                                   isOpen,
                                   onToggle,
                                   instance,
                                 }: {
  discussion: MoodleForumDiscussion;
  discussionId: number;
  isOpen: boolean;
  onToggle: () => void;
  instance: MoodleInstance;
}) {
  const pin = usePinDiscussionMutation(discussionId, instance);
  const lock = useLockDiscussionMutation(discussionId, instance);
  const fav = useToggleFavouriteMutation(discussionId, instance);
  const view = useDiscussionViewMutation(discussionId, instance);

  useEffect(() => {
    if (isOpen) view.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const pinned = Boolean((discussion as any).pinned);
  const locked = Boolean((discussion as any).locked);
  const favourite = Boolean((discussion as any).favourite || (discussion as any).starred);

  return (
    <li className="accordion-item" aria-expanded={isOpen}>
      <div
        className="accordion-summary"
        tabIndex={0}
        role="button"
        aria-controls={`panel-${discussionId}`}
        onClick={onToggle}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
      >
        <div className="summary-main">
          <div className="summary-title">{discussion.name || discussion.subject || `Discussion ${discussionId}`}</div>
          {discussion.message && (
            <div className="summary-snippet" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(discussion.message) }} />
          )}
        </div>
        <div className="summary-actions">
          <button
            type="button"
            className="chip"
            onClick={e => { e.stopPropagation(); pin.mutate(!pinned); }}
            disabled={pin.isPending}
            title="Pin/unpin"
          >
            {pinned ? 'Pinned' : 'Pin'}
          </button>
          <button
            type="button"
            className="chip"
            onClick={e => { e.stopPropagation(); lock.mutate(!locked); }}
            disabled={lock.isPending}
            title="Lock/unlock"
          >
            {locked ? 'Locked' : 'Lock'}
          </button>
          <button
            type="button"
            className="chip"
            onClick={e => { e.stopPropagation(); fav.mutate(); }}
            disabled={fav.isPending}
            title="Toggle favourite"
          >
            {favourite ? '★ Favourite' : '☆ Favourite'}
          </button>
        </div>
      </div>
      {isOpen && (
        <div id={`panel-${discussionId}`} className="accordion-panel">
          <DiscussionDetails
            discussionId={discussionId}
            postId={discussion.id}
            instance={instance}
          />
        </div>
      )}
    </li>
  );
}

export default function MoodleForum({ courseId, forumId, initialDiscussionId, instance }: Props) {
  const activeMoodleUserId = useSelector(
    (state: RootState) => selectActiveMoodleInstanceState(state)?.user?.id
  );

  const resolvedInstance: MoodleInstance =
    instance || (activeMoodleUserId ? 'internal' : 'external');

  const [activeForumId, setActiveForumId] = useState(forumId);
  const [expandedDiscussionId, setExpandedDiscussionId] = useState(initialDiscussionId);
  const [showComposer, setShowComposer] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (forumId) setActiveForumId(forumId);
  }, [forumId]);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeForumId]);

  const accessInfo = useForumAccessInfo(
    activeForumId,
    { enabled: !!activeForumId },
    resolvedInstance
  );

  const canAddDiscussion = useForumCanAddDiscussion(
    activeForumId,
    undefined,
    { enabled: !!activeForumId },
    resolvedInstance
  );

  const discussionsQuery = useForumDiscussions(
    activeForumId,
    {},
    { enabled: !!activeForumId },
    resolvedInstance
  );

  const subscribe = useForumSubscribeMutation(activeForumId || 0, resolvedInstance);
  const forumView = useForumViewMutation(activeForumId || 0, resolvedInstance);
  const addDiscussion = useAddDiscussionMutation(resolvedInstance);

  useEffect(() => {
    if (activeForumId) forumView.mutate();
    // eslint-disable-next-line
  }, [activeForumId, resolvedInstance]);

  const discussions = useMemo(() => {
    const raw: any = discussionsQuery.data;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (raw.discussions) return raw.discussions;
    return [];
  }, [discussionsQuery.data]);

  const paginatedDiscussions = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return discussions.slice(startIndex, endIndex);
  }, [discussions, currentPage, pageSize]);

  const totalPages = Math.ceil(discussions.length / pageSize);

  useEffect(() => {
    if (!expandedDiscussionId && paginatedDiscussions.length > 0) {
      const first = paginatedDiscussions[0];
      const safeId = first?.discussion ?? first.id;
      setExpandedDiscussionId(Number(safeId));
    }
    // eslint-disable-next-line
  }, [paginatedDiscussions.length]);

  const canAdd =
    !!canAddDiscussion.data?.status ||
    !!canAddDiscussion.data?.canadd ||
    !!canAddDiscussion.data?.canadddiscussion ||
    !!canAddDiscussion.data;

  const isLoading = discussionsQuery.isLoading;
  const error = discussionsQuery.error;

  return (
    <div className="forum-wrapper">
      <div className="forum-header">
        {!activeForumId && <h3 className="forum-title">Forums</h3>}
        {activeForumId && (
          <div className="forum-breadcrumb">
            {!forumId && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setActiveForumId(undefined);
                  setExpandedDiscussionId(undefined);
                }}
              >
                ← Back to Forums
              </button>
            )}
            <h3 className="forum-title">Discussions</h3>
          </div>
        )}
      </div>

      {!activeForumId && courseId && (
        <ForumsByCourse
          courseId={courseId}
          instance={resolvedInstance}
          onSelectForum={(id) => {
            setActiveForumId(id);
            setExpandedDiscussionId(undefined);
          }}
        />
      )}

      {activeForumId && (
        <div className="forum-content">
          <div className="forum-actions">
            <div className="left-actions">
              <button
                type="button"
                className="btn"
                onClick={() => subscribe.mutate(!(accessInfo.data?.subscribed ?? false))}
                disabled={subscribe.isPending}
                aria-busy={subscribe.isPending ? 'true' : 'false'}
              >
                {accessInfo.data?.subscribed ? 'Unsubscribe' : 'Subscribe'}
              </button>
            </div>

            <div className="right-actions">
              {canAdd && (
                <button type="button" className="btn btn-primary" onClick={() => setShowComposer((s) => !s)}>
                  {showComposer ? 'Close' : 'New Discussion'}
                </button>
              )}
            </div>
          </div>

          {canAdd && showComposer && (
            <div className="composer-wrap">
              <AddDiscussionForm
                onSubmit={(payload) =>
                  addDiscussion.mutate(
                    { forumid: activeForumId, subject: payload.subject, message: payload.message },
                    {
                      onSuccess: (data) => {
                        setShowComposer(false);
                        discussionsQuery.refetch();
                        setExpandedDiscussionId(data.discussionid);
                      }
                    }
                  )
                }
                submitting={addDiscussion.isPending}
              />
              <p className="hint">Use the &quot;New Discussion&quot; button to create a new topic.</p>
            </div>
          )}

          {isLoading && (
            <div className="center" aria-busy="true">
              <progress aria-label="Loading discussions" />
            </div>
          )}
          {error && (
            <p role="alert" className="error">
              {(error as Error).message}
            </p>
          )}

          {!isLoading && !error && (
            <ul className="accordion-list">
              {paginatedDiscussions.map((d) => {
                const discussionId = d?.discussion ?? d.id;
                return (
                  <DiscussionAccordionItem
                    key={discussionId}
                    discussion={d}
                    discussionId={Number(discussionId)}
                    instance={resolvedInstance}
                    isOpen={expandedDiscussionId === Number(discussionId)}
                    onToggle={() =>
                      setExpandedDiscussionId(cur =>
                        cur === Number(discussionId) ? undefined : Number(discussionId)
                      )
                    }
                  />
                );
              })}
            </ul>
          )}

          {!isLoading && !error && discussions.length > pageSize && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
              >
                ‹ Prev
              </button>
              <div className="page-indicator">
                Page {currentPage + 1} of {totalPages}
              </div>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1))}
                disabled={currentPage >= totalPages - 1}
              >
                Next ›
              </button>
              <select
                className="select page-size-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={30}>30 / page</option>
                <option value={40}>40 / page</option>
              </select>
            </div>
          )}
        </div>
      )}

      {!activeForumId && !courseId && (
        <p className="hint">Select a course or provide a forumId to view discussions.</p>
      )}

      <style>
        {`
          .forum-wrapper { padding: var(--hbr-spacing-md); }
          .forum-header, .forum-breadcrumb, .forum-actions, .left-actions, .right-actions, .pagination {
            display:flex; align-items:center; gap: var(--hbr-spacing-md);
          }
          .forum-actions { justify-content:space-between; margin-bottom: var(--hbr-spacing-md); }
          .pagination { justify-content:center; margin-top: var(--hbr-spacing-md); }
          .forum-title {
            margin: 0;
            font-family: var(--hbr-font-heading);
            font-size: var(--hbr-font-size-xl);
            font-weight: var(--hbr-font-weight-semibold);
          }
          .composer-wrap { margin-bottom: var(--hbr-spacing-lg); }
          .hint { font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-xs); color: rgba(0,0,0,0.6); }
          .center { display:flex; align-items:center; justify-content:center; padding: var(--hbr-spacing-lg); }
          .error { color: red; font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-md); }
          .accordion-list { list-style:none; margin:0; padding:0; }
          .accordion-item + .accordion-item { margin-top: var(--hbr-spacing-sm); }
          .accordion-summary {
            width: 100%;
            display:flex; align-items:flex-start; justify-content:space-between; gap: var(--hbr-spacing-sm);
            padding: var(--hbr-spacing-md);
            border: 1px solid rgba(0,0,0,0.12); border-radius: 10px;
            background:#fff; cursor: pointer; text-align:left;
          }
          .summary-main { flex:1; }
          .summary-title {
            font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-md);
            font-weight: var(--hbr-font-weight-semibold); margin-bottom: var(--hbr-spacing-2xs);
          }
          .summary-snippet {
            font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm);
            color: rgba(0,0,0,0.75); max-height: 4.8em; overflow: hidden;
          }
          .summary-actions { display:flex; gap: var(--hbr-spacing-2xs); }
          .chip {
            display:inline-flex; align-items:center; padding:0.2rem 0.6rem; border-radius:999px;
            border:1px solid rgba(0,0,0,0.15); background:#fff; font-family: var(--hbr-font-body);
            font-size: var(--hbr-font-size-xs); cursor:pointer;
          }
          .chip.danger { color:#b00020; border-color: rgba(176,0,32,0.35); }
          .accordion-panel {
            border: 1px solid rgba(0,0,0,0.12);
            border-top: none;
            border-radius: 0 0 10px 10px;
            background:#fff;
            padding: var(--hbr-spacing-md);
          }
          .page-indicator { font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm); }
          .select.page-size-select {
             padding: 0.4rem 0.6rem; border-radius: 8px; border: 1px solid rgba(0,0,0,0.2);
             background:#fff; font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm);
          }
          .btn {
            display:inline-flex; align-items:center; justify-content:center; gap: var(--hbr-spacing-2xs);
            padding:0.6rem 1rem; border-radius:10px; border:1px solid rgba(0,0,0,0.2);
            background:#fff; color:#0a66ff; cursor:pointer;
            font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm); font-weight: var(--hbr-font-weight-semibold);
          }
          .btn:hover { background: rgba(10,102,255,0.08); }
          .btn-primary { background:#0a66ff; color:#fff; border-color: transparent; }
          .btn-primary:hover { background:#004fe0; }
          .btn-outline { background: transparent; color:#0a66ff; }
        `}
      </style>
    </div>
  );
}